// Operations → the person's score card (Eva, 3 Nov 2026): how they use the platform on one side, what VERA learned about
// them on the other, with what VERA changed on her own (each with Undo) and what she'd suggest next. Each person sees only
// their own. VERANeural's founders see only totals per company (BehaviorTotals), never a person's details.

import { useEffect, useState } from "react";
import { Icon, Orb, useStudio } from "./ui";
import { T } from "../lib/i18n";
import { learned, removeLearned, scores, suggestions, undoChange, useBehavior } from "../lib/behavior";
import { cpFetch } from "../lib/controlPlane";
import { vera } from "../api/vera";

function Meter({ value, label }: { value: number; label: string }) {
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="row between"><span className="mute small">{label}</span><b className="num" style={{ fontSize: 22 }}>{value}</b></div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--line)" }} aria-hidden>
        <div style={{ width: `${value}%`, height: "100%", borderRadius: 3, background: "var(--accent)" }} />
      </div>
    </div>
  );
}

export function ScoreCard() {
  const { go, profile } = useStudio();
  const b = useBehavior();
  const s = scores(b);
  const know = learned(b);
  const ideas = suggestions(b, profile.areas);
  const changes = [...b.changes].reverse().slice(0, 5);
  return (
    <section className="card">
      <div className="row between" style={{ marginBottom: 10 }}>
        <h2>{T("You and VERA")}</h2>
        <span className="faint small">{T("Only you see this.")}</span>
      </div>
      <div className="split">
        <div className="stack" style={{ gap: 10 }}>
          <Meter value={s.usage} label={T("How you use the platform")} />
          <div className="list small">
            <div className="li"><span className="grow">{T("Days active (last 14)")}</span><b>{s.activeDays}</b></div>
            <div className="li"><span className="grow">{T("Pages you use")}</span><b>{s.areasUsed}</b></div>
            <div className="li"><span className="grow">{T("Things handled from your three")}</span><b>{s.handled}</b></div>
            <div className="li"><span className="grow">{T("Times you talked or typed to VERA")}</span><b>{s.veraTotal}</b></div>
          </div>
        </div>
        <div className="stack" style={{ gap: 10 }}>
          <Meter value={s.knows} label={T("How well VERA knows you")} />
          {!know.length ? <p className="faint small" style={{ margin: 0 }}>{T("VERA is still getting to know how you work. Keep going as usual.")}</p> : (
            <div className="list small">{know.map((l) => (
              <div className="li" key={l.id}><Orb size={12} /><span className="grow">{l.text}</span>
                <button className="iconbtn ghost" onClick={() => removeLearned(l.id)} aria-label={T("VERA forgets this")} title={T("VERA forgets this")}><Icon name="x" size={13} /></button></div>))}
            </div>)}
          <p className="faint small" style={{ margin: 0 }}>{T("What VERA keeps from your records and emails is in Settings → What VERA remembers.")}</p>
        </div>
      </div>
      {(changes.length > 0 || ideas.length > 0) && <div className="stack" style={{ gap: 6, marginTop: 12 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>{T("What VERA changed and suggests")}</h3>
        <div className="list small">
          {changes.map((c) => (
            <div className="li" key={c.id}><Icon name="history" size={13} /><span className="grow" style={c.undone ? { textDecoration: "line-through", opacity: 0.6 } : undefined}>{c.text}</span>
              {!c.undone && <button className="btn sm" onClick={() => undoChange(c.id)}>{T("Undo")}</button>}</div>))}
          {ideas.map((i) => (
            <div className="li" key={i}><Icon name="sparkle" size={13} /><span className="grow">{i}</span>
              <button className="btn sm" onClick={() => go("workspace", { ask: i, fresh: true, title: T("Around the platform") })}>{T("Talk with VERA")}</button></div>))}
        </div>
      </div>}
    </section>
  );
}

type Totals = { company: string; people: number; usage: number; knows: number; changes: number };

/** VERANeural's founders: how VERA is learning across companies, numbers only. */
export function BehaviorTotalsCard() {
  const { getToken } = useStudio();
  const [rows, setRows] = useState<Totals[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (vera.mode !== "live") return;
    cpFetch<{ companies: Totals[] }>(getToken, "/v1/admin/behavior").then((r) => setRows(r.companies)).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [getToken]);
  if (vera.mode !== "live") return null;
  return (
    <section className="card">
      <div className="row between" style={{ marginBottom: 8 }}><h2>{T("How VERA is learning, per company")}</h2><span className="faint small">{T("Totals only, never a person's details")}</span></div>
      {error ? <p className="err">{error}</p> : rows === null ? <p className="faint">{T("Loading…")}</p> : !rows.length ? <p className="faint">{T("No one yet.")}</p> : (
        <div className="list small">
          <div className="li faint"><span className="grow">{T("Company")}</span><span style={{ width: 60 }}>{T("People")}</span><span style={{ width: 60 }}>{T("Usage")}</span><span style={{ width: 60 }}>{T("Knows")}</span><span style={{ width: 60 }}>{T("Changes")}</span></div>
          {rows.map((r) => (
            <div className="li" key={r.company}><b className="grow">{r.company}</b><span style={{ width: 60 }}>{r.people}</span><span style={{ width: 60 }}>{r.usage}</span><span style={{ width: 60 }}>{r.knows}</span><span style={{ width: 60 }}>{r.changes}</span></div>))}
        </div>)}
    </section>
  );
}
